import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../elements/icon';
import { MICRO_INGREDIENT_IDS } from '../../../utils/productSuggestions';

const TableRow = ({
  item,
  i,
  onRemoveQuantity,
  ingredientData,
  onAddQuantity,
  prices,
  removeIngredientFromBin,
  euNrv,
  onInputChange,
  isCustomFormula = false,
  originalDose = 0,
  showScientificName = true,
}) => {
  const { t } = useTranslation();
  const [desc, setDesc] = useState(false);
  const maxQuantity = useMemo(() => {
    const configuredMax = isCustomFormula
      ? MICRO_INGREDIENT_IDS.includes(item?.ingredient_id)
        ? parseFloat(ingredientData?.limit_professionals) * 1000
        : parseFloat(ingredientData?.limit_professionals)
      : MICRO_INGREDIENT_IDS.includes(item?.ingredient_id)
        ? parseFloat(ingredientData?.limit_riise) * 1000
        : parseFloat(ingredientData?.limit_riise);

    const apiMaxInUnits = MICRO_INGREDIENT_IDS.includes(item?.ingredient_id)
      ? originalDose * 1000
      : originalDose;

    return apiMaxInUnits > configuredMax ? apiMaxInUnits : configuredMax;
  }, [
    ingredientData?.limit_riise,
    item?.ingredient_id,
    isCustomFormula,
    ingredientData?.limit_professionals,
    originalDose,
  ]);

  const maxQuantityLimit = useMemo(
    () =>
      isCustomFormula
        ? parseFloat(ingredientData?.limit_professionals)
        : originalDose > parseFloat(ingredientData?.limit_riise)
          ? originalDose
          : parseFloat(ingredientData?.limit_riise),
    [isCustomFormula, ingredientData?.limit_professionals, ingredientData?.limit_riise, originalDose],
  );

  const inputRef = useRef(null);

  useEffect(() => {
    const input = inputRef.current;

    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      input.blur();
    };

    input.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      input.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const isMicro = MICRO_INGREDIENT_IDS.includes(item?.ingredient_id);
  const unit = isMicro ? 'µg' : 'mg';

  return (
    <div className="t-border-b t-border-navy/10 t-py-2.5">
      <div className="t-flex t-items-start t-justify-between t-gap-2">
        {/* Left: name + scientific name + info */}
        <div className="t-flex-1 t-min-w-0">
          <div className="t-flex t-items-center t-gap-1.5">
            <p className="!t-m-0 t-text-[12px] t-font-bold t-text-navy">{item.ingredient}</p>
            <Icon
              name="infoFilled"
              color="#005d67"
              size="16"
              className="t-flex-shrink-0 t-cursor-pointer"
              onClick={() => setDesc(!desc)}
            />
          </div>
          {showScientificName && item.scientific_name && (
            <p className="!t-m-0 t-text-[10px] t-text-navy/70 t-leading-snug">
              {item.scientific_name}
            </p>
          )}
        </div>

        {/* Right: input + price + delete */}
        <div className="t-flex t-items-start t-gap-3 t-flex-shrink-0">
          <div className="t-text-right">
            <div className="t-flex t-items-center t-border t-border-navy/20 t-rounded-md t-overflow-hidden">
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                max={maxQuantity}
                value={
                  isMicro
                    ? Math.round(item?.daily_dose * 1000).toString().replace('.', ',')
                    : item?.daily_dose.toString().replace('.', ',')
                }
                className="t-w-[58px] t-px-1.5 t-py-1 t-outline-none t-text-[12px] t-font-semibold t-text-navy t-text-right t-border-0"
                onKeyDown={(e) => {
                  if (['-', '+', 'e', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                  }
                  if (e.key === '.') {
                    e.preventDefault();
                    const input = e.target;
                    const pos = input.selectionStart;
                    const val = input.value;
                    if (!val.includes(',')) {
                      const newVal = val.slice(0, pos) + ',' + val.slice(pos);
                      Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set.call(input, newVal);
                      input.dispatchEvent(new Event('input', { bubbles: true }));
                      setTimeout(() => input.setSelectionRange(pos + 1, pos + 1), 0);
                    }
                  }
                }}
                onChange={(e) => {
                  const normalized = { target: { value: e.target.value.replace(',', '.') } };
                  onInputChange(normalized, i, item, euNrv, ingredientData, isCustomFormula);
                }}
                onBlur={() => {
                  const current = isMicro ? parseFloat(item?.daily_dose) * 1000 : parseFloat(item?.daily_dose);
                  if (current > maxQuantity || current < 0 || isNaN(current)) {
                    const resetValue = isMicro ? originalDose * 1000 : originalDose;
                    onInputChange({ target: { value: resetValue } }, i, item, euNrv, ingredientData, isCustomFormula);
                  }
                }}
              />
              <span className="t-text-[11px] t-text-navy/50 t-pr-2">{unit}</span>
            </div>
            <p className="!t-m-0 t-text-[10px] t-text-navy/40 t-mt-0.5 t-text-center">
              0–{String(maxQuantity).replace('.', ',')} {unit}
            </p>
            {parseFloat(item?.daily_dose) > maxQuantityLimit && (
              <p className="!t-m-0 t-text-[10px] t-text-red-500 t-text-center">max {String(maxQuantity).replace('.', ',')}</p>
            )}
            {parseFloat(item?.daily_dose) < 0 && (
              <p className="!t-m-0 t-text-[10px] t-text-red-500 t-text-center">min 0</p>
            )}
          </div>
          <span className="t-text-[11px] t-text-navy/60 t-min-w-[38px] t-text-right t-pt-1">
            {parseFloat(ingredientData?.eu_nrv) <= 0 ? '*' : `${item.per_eu_nrv}%`}
          </span>
          <span className="t-text-[12px] t-font-semibold t-text-navy t-min-w-[36px] t-text-right t-pt-1">
            {prices[item.ingredient] ?? 0}€
          </span>
          <Icon
            name="circleX"
            size="16"
            color="#9ca3af"
            className="t-cursor-pointer t-pt-1.5"
            onClick={() => removeIngredientFromBin(item)}
          />
        </div>
      </div>

      {/* Expandable description */}
      {desc && (item.static_description || item.description) && (
        <div className="t-bg-[#EEF7F7] t-rounded-lg t-px-3 t-py-2 t-mt-2">
          <p className="!t-m-0 !t-mb-1 t-text-[10px] t-font-bold t-text-[#116D6E] t-uppercase t-tracking-wide t-text-center">
            {t('EditFormula_IngredientDescription')}
          </p>
          <p className="!t-m-0 t-text-xs t-text-[#103642] t-leading-relaxed">
            {item.static_description || item.description}
          </p>
        </div>
      )}
    </div>
  );
};

export default TableRow;
